import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ImageUpload({ 
  onUpload, 
  category = '', 
  multiple = false,
  accept = '.jpg,.jpeg,.png,.gif,.webp',
  maxSize = 10, // MB
  className = ''
}) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const validExts = accept.split(',').map(e => e.replace('.', '').trim());
      if (!validExts.includes(ext)) {
        toast.error(`${file.name}: Tipo file non supportato`);
        return false;
      }
      if (file.size > maxSize * 1024 * 1024) {
        toast.error(`${file.name}: File troppo grande (max ${maxSize}MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      
      if (multiple) {
        validFiles.forEach(file => formData.append('files', file));
        formData.append('category', category);
        
        const res = await axios.post(`${API}/upload/images`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const successful = res.data.uploads.filter(u => !u.error);
        if (successful.length > 0) {
          toast.success(`${successful.length} immagini caricate`);
          onUpload?.(successful);
        }
        
        const failed = res.data.uploads.filter(u => u.error);
        failed.forEach(f => toast.error(`${f.filename}: ${f.error}`));
      } else {
        formData.append('file', validFiles[0]);
        formData.append('category', category);
        
        const res = await axios.post(`${API}/upload/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast.success('Immagine caricata');
        onUpload?.(res.data);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  }, [multiple, category, accept, maxSize, onUpload]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = ''; // Reset input
  };

  return (
    <div className={className}>
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
          ${dragActive ? 'border-[#FF6D00] bg-[#FF6D00]/5' : 'border-gray-300 hover:border-gray-400'}
          ${uploading ? 'pointer-events-none opacity-50' : ''}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
        data-testid="image-upload-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          className="hidden"
          data-testid="image-upload-input"
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-10 h-10 text-[#FF6D00] animate-spin" />
            <p className="text-gray-600">Caricamento in corso...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-10 h-10 text-gray-400" />
            <p className="text-gray-600 font-medium">
              {dragActive ? 'Rilascia qui' : 'Trascina qui o clicca per caricare'}
            </p>
            <p className="text-gray-400 text-sm">
              {multiple ? 'Puoi caricare più file' : 'Un file alla volta'} • Max {maxSize}MB
            </p>
            <p className="text-gray-400 text-xs">
              Formati supportati: JPG, PNG, GIF, WebP
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Image preview with delete option
export function ImagePreview({ 
  images = [], 
  onDelete, 
  editable = true,
  columns = 4,
  className = '' 
}) {
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (image) => {
    if (!editable) return;
    
    setDeleting(image.id || image.url);
    try {
      if (image.id) {
        await axios.delete(`${API}/uploads/${image.id}`);
      }
      onDelete?.(image);
      toast.success('Immagine eliminata');
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    } finally {
      setDeleting(null);
    }
  };

  if (images.length === 0) return null;

  return (
    <div className={`grid gap-4 ${className}`} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {images.map((image, index) => {
        const url = typeof image === 'string' ? image : (image.url || image);
        const id = typeof image === 'string' ? null : image.id;
        const isDeleting = deleting === (id || url);

        return (
          <div 
            key={id || index} 
            className="relative aspect-square rounded-lg overflow-hidden group"
          >
            <img 
              src={url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url}
              alt=""
              className="w-full h-full object-cover"
            />
            {editable && (
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(image); }}
                disabled={isDeleting}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Combined upload component with preview
export function ImageUploader({
  value = [],
  onChange,
  category = '',
  multiple = true,
  maxImages = 10,
  className = ''
}) {
  const images = Array.isArray(value) ? value : (value ? [value] : []);

  const handleUpload = (uploaded) => {
    const newImages = multiple 
      ? [...images, ...(Array.isArray(uploaded) ? uploaded : [uploaded])]
      : [uploaded];
    
    if (newImages.length > maxImages) {
      toast.error(`Massimo ${maxImages} immagini`);
      return;
    }
    
    onChange?.(newImages);
  };

  const handleDelete = (image) => {
    const newImages = images.filter(img => {
      const imgUrl = typeof img === 'string' ? img : img.url;
      const targetUrl = typeof image === 'string' ? image : image.url;
      return imgUrl !== targetUrl;
    });
    onChange?.(newImages);
  };

  return (
    <div className={className}>
      {images.length > 0 && (
        <div className="mb-4">
          <ImagePreview 
            images={images} 
            onDelete={handleDelete}
            columns={Math.min(images.length, 4)}
          />
        </div>
      )}
      
      {(multiple || images.length === 0) && images.length < maxImages && (
        <ImageUpload
          onUpload={handleUpload}
          category={category}
          multiple={multiple}
        />
      )}
      
      {images.length >= maxImages && (
        <p className="text-sm text-gray-500 text-center mt-2">
          Limite massimo di {maxImages} immagini raggiunto
        </p>
      )}
    </div>
  );
}
